import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { PatientTimelineEventControllerApi } from "../../../../../../../services";
import {
  DGT_Pagination,
  getConfigObject,
  PaginatedContent,
  useBaseContent,
} from "../../../../../../../utils/utils";
import { PatientTimelineEvent } from "./use-chronological-record";

interface Props {
  pagination: DGT_Pagination;
  startDate?: string;
  endDate?: string;
  patientUuid?: string;
  xTENANTID?: string;
  enabled?: boolean;
}

/**
 * Paginated variant of the chronological record used to drive infinite scroll
 * on the patient chart timeline. Returns a Spring page of flat timeline events
 * (`data.content` + `data.page`); grouping by day happens in the consumer.
 *
 * @author [Rajkumar Rathod]
 */
export const useChronologicalRecordPaginated = (
  params: Props
): useBaseContent<PatientTimelineEvent> => {
  const {
    pagination,
    startDate,
    endDate,
    patientUuid,
    xTENANTID,
    enabled = true,
  } = params;

  return useQuery({
    queryKey: [
      "chronological-record-paginated",
      pagination,
      startDate,
      endDate,
      patientUuid,
      xTENANTID,
    ],
    refetchOnWindowFocus: false,
    enabled,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      await new PatientTimelineEventControllerApi(getConfigObject())
        .getAllPatientTimelineEventsv2(
          startDate,
          endDate,
          patientUuid,
          pagination.page,
          pagination.pageSize,
          xTENANTID
        )
        // Return the response body as-is; the consumer normalises the shape
        // (Spring page vs server-grouped date-map, enveloped or bare) via
        // extractTimelineEvents. `res.data.data` dug one level too deep and
        // yielded undefined for the bare date-map this endpoint returns.
        .then(
          (res) =>
            res?.data as unknown as PaginatedContent<PatientTimelineEvent>
        ),
  });
};
